import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import closeIcon from "../../assets/close-icon.png";
import checkIcon from "../../assets/check-filled-icon.png";
import jsonAnimation from "../../assets/json-animation.gif";
import { CheckCircle, AlertTriangle } from 'lucide-react';
import {
  toggleCollapseFiles,
  setStepsCompleted
} from '../../store/reducers';
import { useDeclarationSummary } from '../../hooks/useDeclarationSummary';
import { useLanguage, getProcessingModalTranslation } from '../../hooks/useLanguage';
import { apiFetch } from '../../utils/api';
import { JobStreamDisplay } from './JobStreamDisplay';

export const ProcessingModal = ({ isProcessing, setIsProcessing, onProcessComplete, onCancelProcessing }) => {
  const [isSpanish] = useLanguage();
  const processingSteps = [
    getProcessingModalTranslation('addingFiles', isSpanish),
    getProcessingModalTranslation('processingDocuments', isSpanish),
    getProcessingModalTranslation('processingComplete', isSpanish)
  ];

  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [areStepsCompleted, setAreStepsCompleted] = useState(false);
  const [isApiCallInProgress, setIsApiCallInProgress] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [processingStatus, setProcessingStatus] = useState("");
  const [jobId, setJobId] = useState(null);
  const [countdown, setCountdown] = useState(10);
  const countdownTimerRef = useRef(null);
  const handleCloseRef = useRef(null);
  const [isThinking, setIsThinking] = useState(false);
  const dispatch = useDispatch();

  const jsonData = useSelector((state) => state.documents.extractedData);

  const {
    saveChanges
  } = useDeclarationSummary(jsonData);

  // Memoize the status update callback to prevent infinite loops
  const handleStatusUpdate = useCallback((statusUpdate) => {
    if (statusUpdate.areStepsCompleted !== undefined) {
      setAreStepsCompleted(statusUpdate.areStepsCompleted);
    }
    if (statusUpdate.currentStep !== undefined) {
      setCurrentStep(statusUpdate.currentStep);
    }
    if (statusUpdate.completedSteps !== undefined) {
      setCompletedSteps(statusUpdate.completedSteps);
    }
    if (statusUpdate.processingStatus !== undefined) {
      setProcessingStatus(statusUpdate.processingStatus);
      if (statusUpdate.processingStatus === 'error') {
        setHasError(true);
        setErrorMessage(statusUpdate.error || 'An error occurred during document processing.');
      }
    }
    if (statusUpdate.error && !statusUpdate.processingStatus) {
      setHasError(true);
      setErrorMessage(statusUpdate.error);
    }
    if (statusUpdate.jobId && statusUpdate.jobId !== jobId) {
      setJobId(statusUpdate.jobId);
      // Dispatch event to Dashboard to keep stream open
      window.dispatchEvent(new CustomEvent('setActiveJobId', { detail: { jobId: statusUpdate.jobId } }));
    }
    // Check if stream is thinking
    if (statusUpdate.isThinking !== undefined) {
      setIsThinking(statusUpdate.isThinking);
    }
  }, [jobId]);

  const handleProcessDocuments = async () => {
    if (!isProcessing || isApiCallInProgress) return;

    // Reset states at the beginning of processing
    setHasError(false);
    setErrorMessage("");
    setCurrentStep(0);
    setCompletedSteps([]);
    setAreStepsCompleted(false);
    setProcessingStatus("");
    setIsThinking(false);
    dispatch(setStepsCompleted(false));
    setIsApiCallInProgress(true);

    try {
      // Call the document processing function with status update callback
      const responseData = await onProcessComplete((status) => {
        if (status.areStepsCompleted !== undefined) {
          setAreStepsCompleted(status.areStepsCompleted);
        }
        if (status.currentStep !== undefined) {
          setCurrentStep(status.currentStep);
        }
        if (status.completedSteps !== undefined) {
          setCompletedSteps(status.completedSteps);
        }
        if (status.processingStatus !== undefined) {
          setProcessingStatus(status.processingStatus);
          // Handle error status from callback
          if (status.processingStatus === 'error') {
            setHasError(true);
            setErrorMessage(status.error || 'An error occurred during document processing.');
          }
        }
        if (status.error && !status.processingStatus) {
          // Handle error even if processingStatus is not set
          setHasError(true);
          setErrorMessage(status.error);
        }
        if (status.jobId) {
          setJobId(status.jobId);
          // Dispatch event to Dashboard to keep stream open
          window.dispatchEvent(new CustomEvent('setActiveJobId', { detail: { jobId: status.jobId } }));
        }
      });
      
      // If we have a response with a jobId or if there's already a job in queue
      if (responseData && (responseData.jobId || 
          (responseData.status === 'error' && responseData.message?.includes('already have a job in the queue')))) {
        const newJobId = responseData.jobId;
        if (newJobId) {
          setJobId(newJobId);
          // Dispatch event to Dashboard to keep stream open
          window.dispatchEvent(new CustomEvent('setActiveJobId', { detail: { jobId: newJobId } }));
        }
        setCompletedSteps([0]);
        setCurrentStep(1);
        setProcessingStatus("processing");
      } else {
        // Only show error if we don't have a valid response and it's not a queue-related error
        setHasError(true);
        setErrorMessage("Failed to start document processing");
        setCompletedSteps([]);
      }
    } catch (error) {
      // Only show error if it's a network error or other critical failure
      setHasError(true);
      setErrorMessage(error.message || "An error occurred during document processing.");
      setCompletedSteps([]);
    } finally {
      setIsApiCallInProgress(false);
    }
  };

  // This effect runs when isProcessing changes
  useEffect(() => {
    if (isProcessing && !isApiCallInProgress && !hasError) {
      handleProcessDocuments();
    }
  }, [isProcessing]);

  const handleRetry = () => {
    if (!isApiCallInProgress) {
      handleProcessDocuments();
    }
  };

  const handleClose = useCallback(async () => {
    // Don't cancel the job when closing - let it continue in background for suggestions_ready
    // The stream will stay open via JobStreamDisplay even after modal closes
    // Only cancel on explicit cancel (handleCancel)
    // Use setTimeout to avoid React warning about updating component during render
    setTimeout(() => {
      if (onCancelProcessing) onCancelProcessing();
      setAreStepsCompleted(false);
      setIsProcessing(false);
      dispatch(setStepsCompleted(true));
      
      // Set flag to indicate user has a completed job (for restore after timeout)
      sessionStorage.setItem('hasCompletedJob', 'true');
      
      // Store jobId so stream can continue listening for suggestions_ready
      if (jobId) {
        sessionStorage.setItem('activeJobId', jobId);
      }
      
      // Collapse sidebar if it's not already collapsed
      const isSidebarCollapsed = localStorage.getItem('isSidebarCollapsed');
      if (isSidebarCollapsed !== 'true') {
        localStorage.setItem('isSidebarCollapsed', 'true');
        // Dispatch custom event to notify Main.js to update sidebar state
        window.dispatchEvent(new CustomEvent('sidebarCollapse', { detail: { collapsed: true } }));
      }
      
      saveChanges();
    }, 0);
  }, [jobId, onCancelProcessing, dispatch, saveChanges]);

  // Keep handleClose ref updated
  useEffect(() => {
    handleCloseRef.current = handleClose;
  }, [handleClose]);

  const handleCancel = async () => {
    // Cancel current active job
    try {
      if (jobId) {
        console.log('Attempting to cancel job with ID:', jobId);
        const response = await apiFetch(`https://${process.env.REACT_APP_DOCS_API_KEY}/api/queue/${jobId}`, {
          method: 'DELETE',
          headers: {
            'accept': 'application/json'
          }
        });
        
        console.log('Cancel response status:', response.status);
        const responseData = await response.json();
        console.log('Cancel response data:', responseData);

        if (!response.ok) {
          console.error('Failed to cancel job:', responseData);
        }
        
        // Clear active jobId from Dashboard
        window.dispatchEvent(new CustomEvent('clearActiveJobId'));
        sessionStorage.removeItem('activeJobId');
      }
    } catch (error) {
      console.error('Failed to cancel current active job:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack
      });
    }
    if (onCancelProcessing) onCancelProcessing();
    setIsProcessing(false);
    dispatch(toggleCollapseFiles());
    // Reset all processing states
    setCurrentStep(0);
    setCompletedSteps([]);
    setAreStepsCompleted(false);
    setProcessingStatus("");
    setJobId(null);
  };

  const handleCancelJob = async () => {
    if (!jobId) return;
    try {
      await apiFetch(`https://${process.env.REACT_APP_DOCS_API_KEY}/api/queue/${jobId}`, {
        method: 'DELETE',
        headers: {
          'accept': 'application/json'
        }
      });
      setIsProcessing(false);
      setJobId(null);
      // Optionally, show a message or reset modal state
    } catch (error) {
      // Optionally, show an error message
      console.error('Failed to cancel job:', error);
    }
  };

  // Update steps based on processing status
  useEffect(() => {
    if (processingStatus === 'completed') {
      setAreStepsCompleted(true);
      setCurrentStep(2);
      setCompletedSteps([0, 1, 2]);
      setHasError(false); // Clear any errors on completion
    } else if (processingStatus === 'processing') {
      setCurrentStep(1);
      setCompletedSteps([0]);
      setHasError(false); // Clear errors when processing starts
    } else if (processingStatus === 'error') {
      setHasError(true);
    }
  }, [processingStatus]);

  // Countdown timer when steps are completed
  useEffect(() => {
    // Clear any existing timer
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }

    if (areStepsCompleted && !hasError) {
      setCountdown(10);
      countdownTimerRef.current = setInterval(() => {
        setCountdown((prev) => {
          const newCount = prev - 1;
          if (newCount <= 0) {
            if (countdownTimerRef.current) {
              clearInterval(countdownTimerRef.current);
              countdownTimerRef.current = null;
            }
            // Call handleClose using ref to get latest version
            if (handleCloseRef.current) {
              handleCloseRef.current();
            }
            return 0;
          }
          return newCount;
        });
      }, 1000);
    } else {
      setCountdown(10);
    }

    return () => {
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
      }
    };
  }, [areStepsCompleted, hasError]);

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-999">
      <div className={`bg-white p-8 rounded-xl shadow-lg max-w-md w-full max-h-[90vh] overflow-y-auto flex flex-col relative ${areStepsCompleted ? '' : ' h-[90vh]'}`}>
        {areStepsCompleted ? (
          <div className='flex justify-end'>
            <img
              className='h-6 w-6 cursor-pointer'
              src={closeIcon}
              onClick={handleClose}
              alt="Close"
            />
          </div>
        ) : (
          <div className='flex justify-end'>
            <img
              className='h-6 w-6 cursor-pointer'
              src={closeIcon}
              onClick={handleCancel}
              alt="Close"
            />
          </div>
        )}
        <div className="flex flex-col items-center mb-6">
          {hasError ? (
            <AlertTriangle className="p-4 h-24 w-24 mb-4 text-red-500 stroke-red-500" />
          ) : (
            <img
              className="h-24 w-24 mb-4"
              src={areStepsCompleted ? checkIcon : jsonAnimation}
              alt={areStepsCompleted ? "Process Complete" : "Processing"}
            />
          )}
          <h2 className="text-2xl font-bold text-center">
            {hasError ? getProcessingModalTranslation('processingError', isSpanish) : (areStepsCompleted ? getProcessingModalTranslation('documentsProcessed', isSpanish) : getProcessingModalTranslation('processingDocumentsTitle', isSpanish))}
          </h2>
          {hasError && (
            <p className="mt-2 text-gray-500 text-center">{errorMessage}</p>
          )}
        </div>

        {!hasError && (
          <div className="space-y-2 flex-1 flex flex-col">
            {processingSteps.map((step, index) => {
              const isCompleted = completedSteps.includes(index);
              const isActive = index <= currentStep;

              if (!isActive) return null;

              return (
                <div key={index} className="flex items-center py-2">
                  {isCompleted ? (
                    <CheckCircle className="mr-3 w-5 h-5 stroke-primary text-green-500 flex-shrink-0" />
                  ) : (
                    <div className="mr-3 w-5 h-5 flex-shrink-0">
                      <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  )}
                  <span className={`${!isCompleted && index === currentStep
                    ? "text-primary font-medium animate-pulse" : "text-gray-700"}`}>
                    {step}
                  </span>
                </div>
              );
            })}
            
            {/* Job Stream Display - shows real-time thinking and processing updates */}
            {/* Keep stream open even after completion to receive suggestions_ready */}
            {jobId && (
              <div style={{ display: areStepsCompleted ? 'none' : 'block' }}>
                <JobStreamDisplay 
                  jobId={jobId} 
                  onStatusUpdate={handleStatusUpdate}
                />
              </div>
            )}
            
            {areStepsCompleted && (
              <div className="mt-8 text-center flex-1 flex items-end justify-end justify-center">
                <p className="mt-8 text-sm text-gray-600 text-left">
                  {isSpanish ? `Cerrando automáticamente en ${countdown} segundos...` : `Closing automatically in ${countdown} seconds...`}
                </p>
              </div>
            )}
          </div>
        )}


        {hasError && (
          <div className="mt-8 flex justify-center">
            <button
              onClick={handleRetry}
              disabled={isApiCallInProgress}
              className="bg-default hover:bg-primary-dark text-white py-2 px-4 rounded-md flex items-center"
            >
              {isApiCallInProgress ? (
                <>
                  <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  {getProcessingModalTranslation('retrying', isSpanish)}
                </>
              ) : (
                getProcessingModalTranslation('retryProcessing', isSpanish)
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};